/**
 * React Query hooks for notifications. Recipient-scoped server-side; gated only
 * on a present access token (the ProtectedLayout shell guarantees one).
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { listNotifications, type NotificationListResponse } from '../api/notifications.ts';
import { useTokenStore } from '../api/use-api.ts';
import { useAuth } from './auth-context.tsx';

export function useNotifications(unread = false): UseQueryResult<NotificationListResponse> {
  const store = useTokenStore();
  const { accessToken, bootstrapped } = useAuth();
  return useQuery({
    queryKey: ['notifications', { unread }],
    queryFn: () => listNotifications(store, { unread }),
    enabled: bootstrapped && !!accessToken,
    staleTime: 15_000,
    // React Query refetches only on focus/remount, and the header badge never
    // remounts inside the SPA — without a poll, a notification created mid-session
    // is invisible until a full reload (FB-0016). Tab-visible only by default.
    refetchInterval: 30_000,
  });
}

/** Unread count for the header badge. */
export function useUnreadCount(): number {
  const q = useNotifications(true);
  return q.data?.notifications.length ?? 0;
}
